import React from "react";
import headerIcon from "../../../../assets/images/Group 53.svg";

interface PeerComparisonProps {
  yourPercentile: number | string | null;
  message?: string; // optional override
}

const PeerComparison: React.FC<PeerComparisonProps> = ({
  yourPercentile,
  message,
}) => {
  // 1) Convert string|number -> number
  const numericPercentile =
    typeof yourPercentile === "string"
      ? parseFloat(yourPercentile)
      : yourPercentile || 0;

  // 2) Clamp percentile to [0..100]
  const clampedPercentile = Math.max(0, Math.min(numericPercentile, 100));

  // 3) Calculate pointer position based on percentile ranges
  const getPointerPosition = (percentile: number) => {
    if (percentile <= 10) {
      // In the top 10% segment (90-100 percentile range)
      return 95 - (percentile / 10) * 10; // Maps 0-10 to 95-85
    } else if (percentile <= 35) {
      // In the top 25% segment (65-90 percentile range)
      return 85 - ((percentile - 10) / 25) * 35; // Maps 10-35 to 85-50
    } else {
      // In the remaining 65% segment (0-65 percentile range)
      return 50 - ((percentile - 35) / 65) * 50; // Maps 35-100 to 50-0
    }
  };

  const pointerPosition = getPointerPosition(clampedPercentile);

  // 4) Define default messages based on percentile
  const defaultMessage = React.useMemo(() => {
    if (numericPercentile < 33) {
      return "You have a great opportunity to improve quickly with additional practice.";
    } else if (numericPercentile < 66) {
      return "You're on the right track! Keep refining your skills.";
    } else {
      return "Great job! You're ahead of most of your peers.";
    }
  }, [numericPercentile]);

  // 5) finalMessage uses parent's "message" prop if present
  const finalMessage = message ?? defaultMessage;

  // 6) Segments for the bar (placeholders)
  const segments = [
    { width: 50, label: " 65%" },
    { width: 35, label: " 25%" },
    { width: 15, label: "10%" },
  ];

  let cumulative = 0;
  const labelPositions = segments.map((seg) => {
    const mid = cumulative + seg.width / 2;
    cumulative += seg.width;
    return { label: seg.label, position: mid };
  });

  return (
    <div
      className="bg-white rounded-lg shadow-sm h-full flex flex-col"
      style={{ border: "1px solid #E6E8EB" }}
    >
      {/* Header: Icon + Title */}
      <div className="bg-[#F5F6F7] p-4 rounded-t-lg">
        <div className="flex items-center gap-2">
          <img
            src={headerIcon}
            alt="Peer Icon"
            className="w-6 h-6"
            style={{
              filter:
                "invert(27%) sepia(93%) saturate(1862%) hue-rotate(227deg) brightness(89%) contrast(98%)",
            }}
          />

          <h2 className="text-xl font-semibold text-[#1F2024]">
            Peer Comparison
          </h2>
        </div>
      </div>

      <div className="p-6 flex flex-col h-full">
        {/* Message */}
        <p className="text-sm text-[#474A52] mb-6">{finalMessage}</p>

        {/* Main Content: Bar + Labels + Pointer */}
        <div className="flex-1 flex flex-col justify-center mb-12">
          <div className="relative">
            {/* Numeric labels above bar */}
            <div className="absolute top-[-1.5rem] w-full text-sm text-gray-600">
              {labelPositions.map((lp, index) => (
                <span
                  key={index}
                  className="absolute"
                  style={{
                    left: `${lp.position}%`,
                    transform: "translateX(-50%)",
                  }}
                >
                  {lp.label}
                </span>
              ))}
            </div>

            {/* Bar segments */}
            <div className="flex h-2 rounded-full overflow-hidden w-full">
              <div className="bg-[#F7F8F9]" style={{ width: "50%" }} />
              <div className="bg-[#D7D8DB]" style={{ width: "35%" }} />
              <div
                className="bg-[color:var(--color-primary)]"
                style={{ width: "15%" }}
              />
            </div>

            {/* Pointer at exact percentile */}
            <div
              className="absolute -bottom-3 transform -translate-x-1/2"
              style={{ right: `${pointerPosition}%` }}
            >
              <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-b-[8px] border-b-[#FB5D01]" />
              {/* Percentage display commented out */}
            </div>
          </div>
        </div>

        {/* Bottom info row */}
        <div className="flex justify-between text-sm mt-auto">
          <span className="text-[#474A52]">Your Percentile</span>
          {/* Commented out Top percentage display */}
          {/* <span className="text-[color:var(--color-primary)] font-medium">
            Top {100 - numericPercentile}%
          </span> */}
        </div>
      </div>
    </div>
  );
};

export default PeerComparison;
